// AppointmentTabs/ClientDetails.jsx
import React, { useMemo } from "react";
import Classes from "./AppointmentTabs.module.css";
import DropDown from "./StatusDropDown/DropDown";

/* helpers */
const fmt = (d) => (d ? new Date(d).toLocaleDateString() : "—");
const labelFor = (key) => (!key || key === "free" ? "None" : key?.charAt(0).toUpperCase() + key?.slice(1));

function deriveMembership(user) {
  const sub = user?.activeSubscription || {};
  const status = (sub?.status || "none").toLowerCase();
  const start = sub?.current_period_start ? new Date(sub.current_period_start) : null;
  const end = sub?.current_period_end ? new Date(sub.current_period_end) : null;
  const planKey = sub?.plan?.key || user?.subscription || "free";
  const now = Date.now();

  const inWindow = start && end && start.getTime() <= now && end.getTime() >= now;
  const isCanceled = status === "canceled";
  const isNone = status === "none" || !planKey || planKey === "free";
  const isExpired = !!end && end.getTime() < now && !isCanceled;
  const isActive = !isNone && !isCanceled && inWindow;

  let statusText = "None";
  if (isActive) statusText = "Active";
  else if (sub?.cancel_at_period_end && inWindow) statusText = "Cancels at period end";
  else if (isCanceled) statusText = "Canceled";
  else if (isExpired) statusText = "Expired";
  else statusText = "None";

  return {
    planLabel: labelFor(planKey),
    statusText,
    rawStatus: status,
    cancelAtEnd: !!sub?.cancel_at_period_end,
    periodStart: start ? fmt(start) : "—",
    periodEnd: end ? fmt(end) : "—",
    start,
    end,
    planFillupsLimit: Number(sub?.plan?.fillupsLimit || 0), // 0 = unlimited
  };
}

function countUsedThisPeriod(bookings, start, end) {
  if (!Array.isArray(bookings) || !start || !end) return 0;
  const ps = start.getTime();
  const pe = end.getTime();
  return bookings.filter(b => {
    const d = b?.appointment?.date ? new Date(b.appointment.date).getTime() : null;
    if (!d || d < ps || d > pe) return false;
    const st = (b?.status || "").toLowerCase();
    return !(st === "canceled" || st === "cancelled");
  }).length;
}

export default function ClientDetails({ loading, user, bookings }) {
  const email = user?.email || "—";
  const phone = user?.phone || "—";
  const loc = user?.customerProfile?.location || {};
  const address = loc.address ? (
    <>
      {loc.address}
      <br />
      {[loc.city, loc.state, loc.postalCode].filter(Boolean).join(", ")}
    </>
  ) : "—";

  const mem = useMemo(() => deriveMembership(user), [user]);
  const used = useMemo(() => countUsedThisPeriod(bookings, mem.start, mem.end), [bookings, mem.start, mem.end]);

  // remaining: if limit > 0, compute; if 0 => Unlimited; if no active window => "—"
  const remaining = useMemo(() => {
    if (!mem.start || !mem.end) return "—";
    if (mem.planFillupsLimit <= 0) return "Unlimited";
    return Math.max(0, mem.planFillupsLimit - used);
  }, [mem.start, mem.end, mem.planFillupsLimit, used]);

  // for your existing DropDown (binary Active/Inactive)
  const friendlyToggle = mem.statusText === "Active" ? "Active" : "Inactive";

  return (
    <section className={Classes.panel}>
      <h3 className={Classes.title}>Client Details</h3>

      <div className={Classes.row}>
        <p className={Classes.label}>Email</p>
        <p className={`${Classes.value} ${Classes.muted}`}>{loading ? "…" : email}</p>
      </div>
      <div className={Classes.row}>
        <p className={Classes.label}>Phone</p>
        <p className={`${Classes.value} ${Classes.muted}`}>{loading ? "…" : phone}</p>
      </div>
      <div className={Classes.row}>
        <p className={Classes.label}>Address</p>
        <p className={`${Classes.value} ${Classes.muted}`}>{loading ? "…" : address}</p>
      </div>
      <div className={Classes.row}>
        <p className={Classes.label}>Neighborhood</p>
        <p className={`${Classes.value} ${Classes.muted}`}>{loading ? "…" : (loc.neighborhood || "—")}</p>
      </div>
      <div className={Classes.row}>
        <p className={Classes.label}>Gate Code</p>
        <p className={`${Classes.value} ${Classes.muted}`}>{loading ? "…" : (loc.gateCode || "—")}</p>
      </div>
      <div className={Classes.row}>
        <p className={Classes.label}>Parking Spot #</p>
        <p className={`${Classes.value} ${Classes.muted}`}>{loading ? "…" : (loc.parkingSpot || "—")}</p>
      </div>
      <div className={Classes.row}>
        <p className={Classes.label}>Company</p>
        <p className={`${Classes.value} ${Classes.muted}`}>{loading ? "…" : (loc.company || "—")}</p>
      </div>

      {/* Membership (Plan + Status + Period) */}
      <div className={Classes.row}>
        <p className={Classes.label}>Membership</p>
        <div className={Classes.value}>
          <div className={Classes.memWrap}>
            <div className={Classes.memTitle}>
              <strong>{loading ? "…" : mem.planLabel}</strong>
              {!loading && mem.statusText !== "None" && (
                <span
                  className={`${Classes.memBadge} ${mem.statusText === "Active"
                    ? Classes.memOk
                    : mem.statusText === "Cancels at period end"
                      ? Classes.memWarn
                      : Classes.memErr
                    }`}
                >
                  {mem.statusText}
                </span>
              )}
            </div>
            {/* <div className={Classes.memSub}>
              {loading
                ? "…"
                : mem.statusText !== "None"
                ? <>Period: {mem.periodStart} – {mem.periodEnd}</>
                : "—"}
            </div> */}
          </div>
        </div>
      </div>

      {/* Binary toggle (kept for compatibility) */}
      {/* <div className={Classes.row}>
        <p className={Classes.label}>Status</p>
        <DropDown
          isDropDown={false}
          selectedValue={friendlyToggle}
          options={["Active", "Inactive"]}
        />
      </div> */}

      <div className={Classes.row}>
        <p className={Classes.label}>Fill Ups Remaining</p>
        <p className={`${Classes.value} ${Classes.muted}`}>{loading ? "…" : remaining}</p>
      </div>
    </section>
  );
}
